import React, { Fragment, useState, useEffect } from 'react';
import {
  ClipboardList,
  Search,
  Eye,
  Loader2,
  X,
  Download,
  Paperclip,
  FileText,
  AlertCircle,
  CheckCircle,
  Send,
  Mail,
  Copy,
  Clock,
  MessageSquare,
  CheckCircle2,
  XCircle,
  History,
  Link2,
} from 'lucide-react';
import { clientRequirementAnalysisApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, formatDateBR } from '../utils/dateValidation';

// Análise e Crítica dos Requisitos do Cliente

const STATUS_CONFIG = {
  draft: {
    label: 'Aguardando preenchimento',
    color: 'bg-gray-500/20 text-gray-400',
    icon: Send,
  },
  pending_analysis: {
    label: 'Pendente de análise',
    color: 'bg-yellow-500/20 text-yellow-400',
    icon: Clock,
  },
  in_analysis: {
    label: 'Em análise',
    color: 'bg-blue-500/20 text-blue-400',
    icon: Search,
  },
  approved: {
    label: 'Aprovado',
    color: 'bg-green-500/20 text-green-400',
    icon: CheckCircle2,
  },
  correction_needed: {
    label: 'Necessita correção',
    color: 'bg-red-500/20 text-red-400',
    icon: XCircle,
  },
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'draft', label: 'Aguardando preenchimento' },
  { value: 'pending_analysis', label: 'Pendente de análise' },
  { value: 'in_analysis', label: 'Em análise' },
  { value: 'correction_needed', label: 'Necessita correção' },
  { value: 'approved', label: 'Aprovado' },
];

const ClientRequirementAnalysis = () => {
  const [activeTab, setActiveTab] = useState('admin');

  return (
    <div className="space-y-6" data-testid="client-requirement-analysis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardList className="text-green-500" />
            Análise e Crítica dos Requisitos do Cliente
          </h1>
          <p className="text-gray-400 mt-1">Análise e Crítica dos Requisitos do Cliente</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('admin')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'admin' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Administrativo
        </button>
        <button
          onClick={() => setActiveTab('send')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'send' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Enviar Formulário
        </button>
      </div>

      {activeTab === 'admin' && <AdministrativoTab />}
      {activeTab === 'send' && <SendFormTab />}
    </div>
  );
};

// ===================== ABA ADMINISTRATIVO =====================
const AdministrativoTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending_analysis: 0, in_analysis: 0, approved: 0, correction_needed: 0, draft: 0, aptos: 0, nao_aptos: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, statsRes] = await Promise.all([
        clientRequirementAnalysisApi.list({ search: searchTerm, status: statusFilter }),
        clientRequirementAnalysisApi.stats(),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (item) => {
    setDetailLoading(true);
    setDetailItem(item);
    try {
      const res = await clientRequirementAnalysisApi.get(item.id);
      setDetailItem(res.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResend = async (item) => {
    try {
      await clientRequirementAnalysisApi.resendForm(item.id);
      toast.success(`Formulário reenviado para ${item.client_email}`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao reenviar formulário'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    try {
      await clientRequirementAnalysisApi.delete(id);
      toast.success('Registro excluído');
      setDetailItem(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (item) => <td className="font-mono text-white">{item.code}</td> },
    { key: 'cliente', label: 'Cliente', render: (item) => <td className="text-white font-medium">{item.razao_social || '-'}</td> },
    { key: 'cnpj', label: 'CNPJ/CPF', render: (item) => <td className="text-gray-400">{item.cnpj || '-'}</td> },
    { key: 'email', label: 'E-mail', render: (item) => <td className="text-gray-400">{item.client_email || '-'}</td> },
    { key: 'status', label: 'Status', render: (item) => {
      const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
      return <td><span className={`px-2 py-1 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span></td>;
    } },
    { key: 'apto', label: 'Apto a Receber', render: (item) => {
      const apto = item.apto_receber_operacao === 'sim' ? 'Sim' : item.apto_receber_operacao === 'nao' ? 'Não' : '-';
      return <td className="text-gray-400">{apto}</td>;
    } },
    { key: 'created', label: 'Data da Solicitação', render: (item) => <td className="text-gray-400">{formatDateBR(item.created_at ? item.created_at.slice(0, 10) : null)}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (item) => (
      <td>
        <div className="flex gap-1.5 justify-end">
          <button
            onClick={() => openDetail(item)}
            className="p-1.5 text-gray-400 hover:text-green-400 rounded"
            title="Visualizar / Analisar"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={() => handleResend(item)}
            className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
            title="Reenviar formulário"
          >
            <Mail size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'client-requirement-analysis', columns: COLUMNS });

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Pendentes de Análise" value={stats.pending_analysis || 0} tone="yellow" />
        <StatCard label="Em Análise" value={stats.in_analysis || 0} tone="blue" />
        <StatCard label="Necessita Correção" value={stats.correction_needed || 0} tone="red" />
        <StatCard label="Aprovados" value={stats.approved || 0} tone="green" />
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-700">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por razão social, CNPJ, código ou e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input px-3 w-full sm:w-60"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 pt-4">
              <p className="text-sm text-gray-400">
                {items.length} {items.length === 1 ? 'análise' : 'análises'}
              </p>
              <div className="flex items-center gap-2">
                <ExportButtons module="client_requirement_analysis" moduleName="Análise e Crítica dos Requisitos" filters={{}} />
                <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
              </div>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    {visibleColumns.map(col => (
                      <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="py-8 text-center text-gray-500">
                        Nenhuma análise encontrada
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id}>
                        {visibleColumns.map(col => (
                          <Fragment key={col.key}>{col.render(item)}</Fragment>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {detailItem && (
        <DetailModal
          item={detailItem}
          loading={detailLoading}
          onClose={() => setDetailItem(null)}
          onResend={handleResend}
          onDelete={handleDelete}
          onChanged={loadData}
        />
      )}
    </>
  );
};

const StatCard = ({ label, value, tone }) => {
  const tones = {
    yellow: 'border-yellow-500/30 text-yellow-400',
    blue: 'border-blue-500/30 text-blue-400',
    red: 'border-red-500/30 text-red-400',
    green: 'border-green-500/30 text-green-400',
    gray: 'border-gray-500/30 text-gray-400',
  };
  return (
    <div className={`card p-4 border ${tones[tone] || tones.gray}`}>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </div>
  );
};

const FIELD_LABELS = {
  razao_social: 'Razão Social',
  cnpj: 'CNPJ',
  inscricao_estadual: 'Inscrição Estadual',
  endereco: 'Endereço',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'UF',
  cep: 'CEP',
  contato: 'Contato',
  email: 'E-mail',
  telefone: 'Telefone',
  cargo: 'Cargo',
  atividade_principal: 'Atividade Principal',
  escalonamento_cliente: 'Escalonamento do Cliente',
  dimensoes_produto_embalagem: 'Dimensões produto/embalagem',
  produto_sensivel_restricao: 'Produto sensível / restrição',
  enviar_book_catalogo: 'Enviar book/catálogo',
  outros_produtos: 'Outros produtos',
  escopo_outros: 'Escopo - Outros',
  duracao_contrato_anos: 'Duração do Contrato (anos)',
  data_inicio_operacao: 'Data início da operação',
  previsao_aumento_volume: 'Previsão de aumento de volume',
  regime_fiscal: 'Regime Fiscal',
  fluxo_fiscal: 'Fluxo Fiscal',
  condicoes_temperatura: 'Condições de Temperatura',
  responsabilidade_seguro: 'Responsabilidade do Seguro',
  recebimento_entrada_min: 'Entrada mensal - mín',
  recebimento_entrada_med: 'Entrada mensal - méd',
  recebimento_entrada_max: 'Entrada mensal - máx',
  forma_envio_operador: 'Forma de envio ao Operador',
  logistica_reversa: 'Logística Reversa',
  recebimentos_frequencia: 'Frequência de recebimentos',
  conferencia_tipo: 'Tipo de conferência',
  registro_fotografico: 'Registro fotográfico',
  recebimento_informacoes_adicionais: 'Recebimento - Informações Adicionais',
  quantidade_skus: 'Quantidade de SKUs',
  atividades_armazenagem: 'Atividades de armazenagem',
  tipo_palete: 'Tipo de palete',
  restricoes_embalagem: 'Restrições de embalagem',
  controle_operacao: 'Controle da operação',
  fispq_fds_armazenagem: 'FISPQs/FDS (armazenagem)',
  requisitos_especificos_armazenagem: 'Requisitos específicos (armazenagem)',
  restricao_empilhamento: 'Restrição de empilhamento',
  area_segregada: 'Área segregada / controle de acesso',
  dimensoes_palete: 'Dimensões do Palete',
  area_utilizada_atualmente: 'Área utilizada atualmente',
  inventario_auditoria: 'Inventário/auditoria',
  armazenagem_informacoes_adicionais: 'Armazenagem - Informações Adicionais',
  expedicao_saida_min: 'Saída mensal - mín',
  expedicao_saida_med: 'Saída mensal - méd',
  expedicao_saida_max: 'Saída mensal - máx',
  fracionamento_caixa: 'Fracionamento de Caixa',
  repaletizacao: 'Repaletização',
  requisitos_especiais_expedicao: 'Requisitos especiais para expedição',
  expedicao_informacoes_adicionais: 'Expedição - Informações Adicionais',
  qtd_nf_mensal: 'NFs emitidas por mês',
  qtd_media_pedidos_mensal: 'Pedidos por mês',
  qtd_nf_por_pedido: 'NF por pedido',
  qtd_linhas_por_nf: 'Linhas por NF',
  qtd_volumes_por_nf: 'Volumes por NF',
  qtd_media_itens_pedido: 'Itens por pedido',
  pedidos_emergenciais: 'Pedidos emergenciais',
  operacao_fora_horario: 'Operação fora do horário',
  codigo_barras_padrao: 'Código de Barras',
  controle_lote: 'Controle de lote',
  sistema_controle_estoque: 'Sistema de Controle de Estoque',
  integracao_sistemas: 'Integração entre sistemas',
  sistema_oficial: 'Sistema oficial',
  tipo_rastreabilidade: 'Tipo de rastreabilidade',
  anexo_faturamento_descricao: 'Anexo faturamento (txt)',
  anexo_proposta_descricao: 'Anexo proposta/documento',
  carga_dedicada: 'Carga dedicada',
  tipo_produto_transporte: 'Tipo de produto transportado',
  fispq_fds_transporte: 'FISPQs/FDS (transporte)',
  requisitos_especificos_transporte: 'Requisitos específicos / temperatura',
  horario_corte: 'Horário de corte',
  valor_produtos: 'Valor dos produtos',
  slas_recebimento_processamento_expedicao: 'SLAs de recebimento/processamento/expedição',
  indicadores_kpis: 'Indicadores/KPIs',
  insumos_necessarios: 'Insumos necessários',
  responsavel_insumos: 'Responsável pelos insumos',
  exigencias_seguranca_confidencialidade: 'Exigências segurança/compliance',
  observacoes: 'Observações',
  apto_receber_operacao: 'Apto a receber a operação',
  apto_receber_observacao: 'Observação da avaliação Place',
};

const SCOPE_LABELS = {
  logistica_integrada: 'Logística Integrada',
  etiquetagem: 'Etiquetagem',
  armazenagem: 'Armazenagem',
  picking_packing: 'Picking/Packing',
  operacao_inhouse: 'Operação "In-House"',
  montagem_kits: 'Montagem de Kits',
  transportes: 'Transportes',
};

const CHARGE_LABELS = {
  ad_valorem: 'Ad. Valorem',
  pico_pallets: 'Pico de pallets',
  pallets_recebidos: 'Pallets recebidos',
  linhas_atendidas: 'Linhas atendidas',
  volumes_pallets_expedidos: 'Volumes/pallets expedidos',
  descarga_veiculos: 'Descarga de veículos',
  utilizacao_internet: 'Utilização de internet',
  embalagens_expedicao: 'Embalagens para expedição',
  fitas_adesivas: 'Fitas adesivas',
  etiquetagem: 'Etiquetagem',
  area_ocupada: 'Área ocupada (m²)',
  area_segregada: 'Área segregada',
  area_refrigerada: 'Área refrigerada',
  linhas_nf: 'Linhas de NF',
  pallets_repaletizados: 'Pallets repaletizados',
  reembalamento: 'Reembalamento',
  pesagem_cubagem: 'Pesagem/cubagem',
  fracionamento_graos: 'Fracionamento em grãos',
  fracionamento: 'Fracionamento',
  conferencia_itens: 'Conferência de itens',
  filmar_pallets: 'Filmar pallets',
  lonamento: 'Lonamento',
  impressao_a4: 'Impressão de A4',
  pallets_locados: 'Pallets locados',
  pallet_out: 'Pallet out',
};

export const RequirementAnalysisDetails = ({ item }) => {
  const renderField = (key) => {
    const value = item[key];
    if (value === null || value === undefined || value === '') return null;
    return (
      <div key={key} className="md:col-span-1">
        <p className="text-xs text-gray-500">{FIELD_LABELS[key] || key}</p>
        <p className="text-sm text-gray-200 whitespace-pre-wrap">{String(value)}</p>
      </div>
    );
  };

  const sectionKeys = [
    ['razao_social', 'cnpj', 'inscricao_estadual', 'endereco', 'bairro', 'cidade', 'uf', 'cep'],
    ['contato', 'email', 'telefone', 'cargo', 'atividade_principal', 'escalonamento_cliente'],
  ];

  return (
    <div className="space-y-6">
      {/* Identificação */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">1. Identificação do Cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {sectionKeys[0].map(renderField)}
        </div>
      </div>

      {/* Contato */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">Contato</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {sectionKeys[1].map(renderField)}
        </div>
      </div>

      {/* Produtos */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">2. Tipos e características dos produtos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {renderField('dimensoes_produto_embalagem')}
          {renderField('produto_sensivel_restricao')}
          {renderField('enviar_book_catalogo')}
          {renderField('outros_produtos')}
        </div>
      </div>

      {/* Escopo */}
      {(item.escopo || []).length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">3. Escopo</h3>
          <div className="flex flex-wrap gap-2">
            {item.escopo.map((k) => (
              <span key={k} className="px-2 py-1 rounded-full text-xs bg-green-500/15 text-green-400">
                {SCOPE_LABELS[k] || k}
              </span>
            ))}
          </div>
          {item.escopo_outros && <p className="text-sm text-gray-300 mt-2">Outros: {item.escopo_outros}</p>}
        </div>
      )}

      {/* Operação */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">4. Características da Operação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {renderField('duracao_contrato_anos')}
          {renderField('data_inicio_operacao')}
          {renderField('previsao_aumento_volume')}
          {renderField('regime_fiscal')}
          {renderField('fluxo_fiscal')}
          {renderField('condicoes_temperatura')}
          {renderField('responsabilidade_seguro')}
        </div>
      </div>

      {/* Recebimento */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">5. Atividade de Recebimento</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
          {renderField('recebimento_entrada_min')}
          {renderField('recebimento_entrada_med')}
          {renderField('recebimento_entrada_max')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-2">
          {renderField('forma_envio_operador')}
          {renderField('logistica_reversa')}
          {renderField('recebimentos_frequencia')}
          {renderField('conferencia_tipo')}
          {renderField('registro_fotografico')}
          {renderField('recebimento_informacoes_adicionais')}
        </div>
      </div>

      {/* Armazenagem */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">6. Atividade de Armazenagem</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {renderField('quantidade_skus')}
          {renderField('atividades_armazenagem')}
          {renderField('tipo_palete')}
          {renderField('restricoes_embalagem')}
          {renderField('controle_operacao')}
          {renderField('fispq_fds_armazenagem')}
          {renderField('requisitos_especificos_armazenagem')}
          {renderField('restricao_empilhamento')}
          {renderField('area_segregada')}
          {renderField('dimensoes_palete')}
          {renderField('area_utilizada_atualmente')}
          {renderField('inventario_auditoria')}
          {renderField('armazenagem_informacoes_adicionais')}
        </div>
      </div>

      {/* Expedição */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">7. Atividade de Expedição</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
          {renderField('expedicao_saida_min')}
          {renderField('expedicao_saida_med')}
          {renderField('expedicao_saida_max')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-2">
          {renderField('fracionamento_caixa')}
          {renderField('repaletizacao')}
          {renderField('requisitos_especiais_expedicao')}
          {renderField('expedicao_informacoes_adicionais')}
        </div>
      </div>

      {/* Processamento */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">8. Processamento de Pedidos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {renderField('qtd_nf_mensal')}
          {renderField('qtd_media_pedidos_mensal')}
          {renderField('qtd_nf_por_pedido')}
          {renderField('qtd_linhas_por_nf')}
          {renderField('qtd_volumes_por_nf')}
          {renderField('qtd_media_itens_pedido')}
          {renderField('pedidos_emergenciais')}
          {renderField('operacao_fora_horario')}
        </div>
      </div>

      {/* Tecnologia */}
      <div>
        <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">9. Tecnologia</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {renderField('codigo_barras_padrao')}
          {renderField('controle_lote')}
          {renderField('sistema_controle_estoque')}
          {renderField('integracao_sistemas')}
          {renderField('sistema_oficial')}
          {renderField('tipo_rastreabilidade')}
        </div>
      </div>

      {/* Distribuição / Transporte / SLA / Adicionais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {renderField('anexo_faturamento_descricao')}
        {renderField('carga_dedicada')}
        {renderField('tipo_produto_transporte')}
        {renderField('fispq_fds_transporte')}
        {renderField('requisitos_especificos_transporte')}
        {renderField('horario_corte')}
        {renderField('valor_produtos')}
        {renderField('anexo_proposta_descricao')}
        {renderField('slas_recebimento_processamento_expedicao')}
        {renderField('indicadores_kpis')}
        {renderField('insumos_necessarios')}
        {renderField('responsavel_insumos')}
        {renderField('exigencias_seguranca_confidencialidade')}
        {renderField('observacoes')}
      </div>

      {/* Checklist de Cobrança */}
      {(item.cobranca_items || []).length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3">Atividades a cobrar do cliente</h3>
          <div className="flex flex-wrap gap-2">
            {item.cobranca_items.map((k) => (
              <span key={k} className="px-2 py-1 rounded-full text-xs bg-blue-500/15 text-blue-400">
                {CHARGE_LABELS[k] || k}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Anexos */}
      {(item.attachments || []).length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Paperclip size={16} /> Anexos ({item.attachments.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {item.attachments.map((att) => (
              <button
                key={att.id}
                onClick={async () => {
                  try {
                    const res = await clientRequirementAnalysisApi.downloadAttachment(att.id);
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', att.file_name || 'documento.pdf');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    toast.error('Erro ao baixar anexo');
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-700 text-sm text-gray-200 hover:border-green-500/50"
              >
                <FileText size={16} className="text-green-500" />
                {att.file_name}
                <Download size={14} className="text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {(item.timeline || []).length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <History size={16} /> Histórico
          </h3>
          <div className="space-y-2">
            {item.timeline.map((t, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="text-gray-500 whitespace-nowrap">{formatDateBR(t.date ? t.date.slice(0, 10) : null)}</span>
                <span className="text-gray-300">{t.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observação de correção */}
      {item.status === 'correction_needed' && item.admin_observation && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 font-medium flex items-center gap-2 mb-1">
            <AlertCircle size={16} /> Correção solicitada
          </p>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{item.admin_observation}</p>
        </div>
      )}
    </div>
  );
};

const DetailModal = ({ item, loading, onClose, onResend, onDelete, onChanged }) => {
  const [analysisAction, setAnalysisAction] = useState(null);
  const [rejection, setRejection] = useState('');
  const [processing, setProcessing] = useState(false);

  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;

  const canAnalyze = item.status === 'pending_analysis' || item.status === 'correction_needed';

  const handleStartAnalysis = async () => {
    setProcessing(true);
    try {
      await clientRequirementAnalysisApi.startAnalysis(item.id);
      toast.success('Análise iniciada');
      onChanged();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao iniciar análise'));
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm(`Aprovar a análise "${item.code}"?`)) return;
    setProcessing(true);
    try {
      await clientRequirementAnalysisApi.approve(item.id);
      toast.success('Análise aprovada com sucesso');
      onChanged();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao aprovar'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejection.trim()) {
      toast.error('Informe a observação para solicitar correção');
      return;
    }
    setProcessing(true);
    try {
      await clientRequirementAnalysisApi.reject(item.id, rejection.trim());
      toast.success('Correção solicitada');
      setAnalysisAction(null);
      setRejection('');
      onChanged();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao solicitar correção'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <span className="font-mono text-green-400">{item.code}</span>
              <span className={`px-2 py-1 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
            </h2>
            <p className="text-sm text-gray-400">{item.client_email || '-'}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {item.status === 'draft' && (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-yellow-400 font-medium flex items-center gap-2 mb-1">
                  <Clock size={16} /> Formulário ainda não preenchido
                </p>
                <p className="text-sm text-gray-300">
                  O cliente ainda não preencheu e enviou este formulário pelo link. Nenhum dado foi recebido até o momento.
                </p>
              </div>
            )}
            <RequirementAnalysisDetails item={item} />

            {/* Ações de análise */}
            {canAnalyze && (
              <div className="flex flex-col gap-3 border-t border-gray-700 pt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleStartAnalysis}
                    disabled={processing}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Search size={18} /> Iniciar análise
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="btn-primary flex items-center gap-2"
                  >
                    <CheckCircle size={18} /> Aprovar
                  </button>
                  <button
                    onClick={() => setAnalysisAction(analysisAction === 'reject' ? null : 'reject')}
                    disabled={processing}
                    className="btn-secondary flex items-center gap-2 text-red-400 border-red-500/40"
                  >
                    <XCircle size={18} /> Solicitar correção
                  </button>
                </div>
                {analysisAction === 'reject' && (
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={rejection}
                      onChange={(e) => setRejection(e.target.value)}
                      rows={3}
                      className="input w-full"
                      placeholder="Informe o motivo / observação da correção..."
                    />
                    <button onClick={handleReject} disabled={processing} className="btn-primary flex items-center gap-2 self-start bg-red-600 hover:bg-red-700">
                      <MessageSquare size={18} /> Enviar correção
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Ações gerais */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-700 pt-4">
              <button
                onClick={() => onResend(item)}
                className="btn-secondary flex items-center gap-2"
              >
                <Mail size={18} /> Reenviar formulário
              </button>
              <button
                onClick={() => onDelete(item.id)}
                className="btn-secondary flex items-center gap-2 text-red-400 border-red-500/40"
              >
                <XCircle size={18} /> Excluir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== ABA ENVIAR FORMULÁRIO =====================
const SendFormTab = () => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [copyDone, setCopyDone] = useState(false);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      setLoadingDrafts(true);
      const res = await clientRequirementAnalysisApi.list({ status: 'draft' });
      setDrafts(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar formulários enviados');
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const emailTrim = email.trim();
    if (!emailTrim) {
      toast.error('Informe o e-mail do cliente ou responsável');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error('Informe um e-mail válido');
      return;
    }
    setSending(true);
    try {
      const res = await clientRequirementAnalysisApi.sendForm(emailTrim);
      const item = res.data;
      setLastSent({
        email: emailTrim,
        code: item.code,
        link: `${window.location.origin}/analise-critica/${item.request_token}`,
        emailSent: item.email_sent !== false,
        message: item.message,
      });
      setEmail('');
      setCopyDone(false);
      if (item.email_sent === false) {
        toast.warning(item.message || 'Não foi possível enviar o e-mail. O link está disponível abaixo.');
      } else {
        toast.success('Formulário enviado com sucesso');
      }
      loadDrafts();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar formulário'));
    } finally {
      setSending(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopyDone(true);
      toast.success('Link copiado');
    }).catch(() => toast.error('Não foi possível copiar o link'));
  };

  const handleResend = async (item) => {
    try {
      await clientRequirementAnalysisApi.resendForm(item.id);
      toast.success(`Formulário reenviado para ${item.client_email}`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao reenviar formulário'));
    }
  };

  const getLink = (item) => `${window.location.origin}/analise-critica/${item.request_token}`;

  return (
    <div className="space-y-6">
      {/* Enviar formulário */}
      <div className="card p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
            <Send size={20} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Enviar formulário de análise e crítica</h2>
            <p className="text-sm text-gray-400">
              O cliente receberá um link exclusivo por e-mail para preencher a análise e anexar os documentos.
            </p>
          </div>
        </div>

        <form onSubmit={handleSend} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              placeholder="E-mail do cliente ou responsável"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2 justify-center" disabled={sending}>
            {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            Enviar formulário
          </button>
        </form>

        {lastSent && (
          <div className={`mt-4 p-4 rounded-lg border ${lastSent.emailSent ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
            <p className={`font-medium flex items-center gap-2 ${lastSent.emailSent ? 'text-green-400' : 'text-yellow-400'}`}>
              {lastSent.emailSent ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              {lastSent.emailSent
                ? `Formulário enviado para ${lastSent.email}`
                : `E-mail não enviado — ${lastSent.message || 'copie o link e envie manualmente'}`}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Solicitação <span className="text-green-400 font-mono">{lastSent.code}</span>. Compartilhe o link abaixo
              caso o cliente não receba o e-mail:
            </p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <input
                readOnly
                value={lastSent.link}
                onFocus={(e) => e.target.select()}
                className="input w-full text-xs font-mono"
              />
              <button
                type="button"
                onClick={() => copyLink(lastSent.link)}
                className="btn-secondary flex items-center gap-2 whitespace-nowrap"
              >
                {copyDone ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                Copiar link
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Links enviados aguardando preenchimento */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
            <Clock size={16} className="text-green-500" />
            Formulários enviados aguardando preenchimento
          </h3>
          <span className="badge badge-gray">{drafts.length}</span>
        </div>
        {loadingDrafts ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-green-500" size={28} />
          </div>
        ) : drafts.length === 0 ? (
          <p className="p-6 text-center text-gray-500 text-sm">Nenhum formulário pendente de preenchimento</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>E-mail</th>
                  <th>Data de Envio</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((item) => (
                  <tr key={item.id}>
                    <td className="font-mono text-white">{item.code}</td>
                    <td className="text-gray-400">{item.client_email}</td>
                    <td className="text-gray-400">{formatDateBR(item.created_at ? item.created_at.slice(0, 10) : null)}</td>
                    <td>
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">
                        Aguardando preenchimento
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button
                          onClick={() => copyLink(getLink(item))}
                          className="p-1.5 text-gray-400 hover:text-green-400 rounded"
                          title="Copiar link"
                        >
                          <Link2 size={18} />
                        </button>
                        <button
                          onClick={() => handleResend(item)}
                          className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
                          title="Reenviar formulário por e-mail"
                        >
                          <Mail size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientRequirementAnalysis;